import { reverseKey } from './functions';

const QUERY_OPTIONS = {
  FILTER_AGE: 'fage',
  FILTER_LOCATION: 'floc',
  FIRST_NAME: 'fn',
  MIDDLE_NAME: 'mn',
  LAST_NAME: 'ln',
  PHONE_ADVANCED: 'phn_advance',
  PHONE: 'phn',
  COUNTRY_CODE: 'ccode',
  EMAIL: 'em',
  USERNAME: 'un',
  LOCATION_STRING_ADVANCE: 'loc_str_advance',
  LOCATION_STRING: 'loc_str',
  LOCATION_ID: 'loc_id',
  HOUSE: 'house',
  STREET: 'street',
  JOB: 'org',
  JOB_TITLE: 'title',
  RELATIONSHIP_FIRST_NAME: 'rel_fn',
  RELATIONSHIP_MIDDLE_NAME: 'rel_mn',
  RELATIONSHIP_LAST_NAME: 'rel_ln',
  EDUCATIONS: 'school',
  DEGREE: 'degree',
  DOB: 'dob',
};

const QUERY_TO_FULL = {
  [QUERY_OPTIONS.FIRST_NAME]: 'firstName',
  [QUERY_OPTIONS.MIDDLE_NAME]: 'middleName',
  [QUERY_OPTIONS.LAST_NAME]: 'lastName',
  [QUERY_OPTIONS.PHONE]: 'phone',
  [QUERY_OPTIONS.EMAIL]: 'email',
  [QUERY_OPTIONS.USERNAME]: 'username',
  [QUERY_OPTIONS.LOCATION_STRING]: 'locationString',
  [QUERY_OPTIONS.LOCATION_ID]: 'locationId',
  [QUERY_OPTIONS.JOB]: 'org',
  [QUERY_OPTIONS.JOB_TITLE]: 'title',
};

const QUERY_TO_ADVANCED = {
  [QUERY_OPTIONS.FIRST_NAME]: 'names',
  [QUERY_OPTIONS.PHONE_ADVANCED]: 'phones',
  [QUERY_OPTIONS.EMAIL]: 'emails',
  [QUERY_OPTIONS.USERNAME]: 'usernames',
  [QUERY_OPTIONS.RELATIONSHIP_FIRST_NAME]: 'relationships',
  [QUERY_OPTIONS.LOCATION_STRING_ADVANCE]: 'addresses',
  [QUERY_OPTIONS.LOCATION_STRING]: 'addresses',
  [QUERY_OPTIONS.JOB]: 'jobs',
  [QUERY_OPTIONS.EDUCATIONS]: 'educations',
  [QUERY_OPTIONS.DOB]: 'dobs',
};

const FULL_TO_QUERY = reverseKey(QUERY_TO_FULL);

/**
 * Parses a param object to url query params
 * @param  {} params
 */
export function parseParams(params) {
  const keys = Object.keys(params);
  let options = '';

  keys.forEach(key => {
    const isParamTypeObject = typeof params[key] === 'object';
    const isParamTypeArray = isParamTypeObject && params[key] && params[key].length >= 0;

    if (!isParamTypeObject) {
      options += `${key}=${encodeURIComponent(params[key])}&`;
    }

    if (isParamTypeObject && isParamTypeArray) {
      params[key].forEach(element => {
        options += `${key}=${encodeURIComponent(element)}&`;
      });
    }
  });

  return options ? options.slice(0, -1) : options;
}

/**
 * Parses a given pipl search query string
 * @param {string} queryString - The query string
 * @returns {{QUERY_OPTIONS}} - Search query object:
 */
export function parseSearchQuery(queryString) {
  let query = queryString;
  if (queryString.charAt(0) === '?') {
    query = queryString.substr(1, queryString.length);
  }

  const queryObject = createQueryObjectFromString(query);

  return queryObject;
}

export function getQueryObjectItem(queryObject, key, index) {
  return queryObject[key] !== undefined && queryObject[key][index] ? queryObject[key][index] : '';
}

function createQueryObjectFromString(queryString) {
  const queryObject = {};
  const splittedQuery = queryString.split('&');

  splittedQuery.forEach(queryItem => {
    const decodedQuery = decodeURIComponent(queryItem.replace(/\+/g, ' '));
    const [key, value] = decodedQuery.split(/=(.*)/);

    queryObject[key] = queryObject[key] || [];
    queryObject[key].push(value);
  });

  return queryObject;
}

export function freeFormObjectToQuery(form) {
  return Object.keys(form).reduce((acc, key) => {
    if (form[key] && FULL_TO_QUERY[key]) {
      acc[FULL_TO_QUERY[key]] = form[key];
    }

    return acc;
  }, {});
}

export function queryToFreeFormObject(queryObject) {
  return Object.keys(queryObject).reduce((acc, key) => {
    if (QUERY_TO_FULL[key]) {
      acc[QUERY_TO_FULL[key]] = Array.isArray(queryObject[key]) ? queryObject[key][0] : queryObject[key];
    }

    return acc;
  }, createEmptyFreeFormObject());
}

export function queryObjectToAdvancedObject(query) {
  const HANDLER = {
    [QUERY_OPTIONS.FIRST_NAME]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          first: item,
          last: getQueryObjectItem(queryObject, QUERY_OPTIONS.LAST_NAME, index),
          middle: getQueryObjectItem(queryObject, QUERY_OPTIONS.MIDDLE_NAME, index),
        };
      });
    },
    [QUERY_OPTIONS.PHONE_ADVANCED]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          number: item.toString(),
          country_code: getQueryObjectItem(queryObject, QUERY_OPTIONS.COUNTRY_CODE, index).toString(),
        };
      });
    },
    [QUERY_OPTIONS.PHONE]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          number: item.toString(),
          country_code: getQueryObjectItem(queryObject, QUERY_OPTIONS.COUNTRY_CODE, index).toString(),
        };
      });
    },
    [QUERY_OPTIONS.RELATIONSHIP]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          first: item,
          last: getQueryObjectItem(queryObject, QUERY_OPTIONS.RELATIONSHIP_LAST_NAME, index),
          middle: getQueryObjectItem(queryObject, QUERY_OPTIONS.RELATIONSHIP_MIDDLE_NAME, index),
        };
      });
    },
    [QUERY_OPTIONS.EDUCATIONS]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          school: item,
          degree: getQueryObjectItem(queryObject, QUERY_OPTIONS.DEGREE, index),
        };
      });
    },
    [QUERY_OPTIONS.JOB]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          org: item,
          title: getQueryObjectItem(queryObject, QUERY_OPTIONS.JOB_TITLE, index),
        };
      });
    },
    [QUERY_OPTIONS.EMAIL]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          email: item,
        };
      });
    },
    [QUERY_OPTIONS.DOB]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          dob: item,
        };
      });
    },
    [QUERY_OPTIONS.USERNAME]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          username: item,
        };
      });
    },
    [QUERY_OPTIONS.RELATIONSHIP_FIRST_NAME]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          first: item,
          middle: getQueryObjectItem(queryObject, QUERY_OPTIONS.RELATIONSHIP_MIDDLE_NAME, index),
          last: getQueryObjectItem(queryObject, QUERY_OPTIONS.RELATIONSHIP_LAST_NAME, index),
        };
      });
    },
    [QUERY_OPTIONS.LOCATION_STRING_ADVANCE]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          locStr: item,
          locId: getQueryObjectItem(queryObject, QUERY_OPTIONS.LOCATION_ID, index),
          street: getQueryObjectItem(queryObject, QUERY_OPTIONS.STREET, index),
          house: getQueryObjectItem(queryObject, QUERY_OPTIONS.HOUSE, index),
        };
      });
    },
  };

  return Object.keys(query).reduce((acc, key) => {
    if (QUERY_TO_ADVANCED[key] && HANDLER[key] && query[key].length > 0) {
      acc[QUERY_TO_ADVANCED[key]] = HANDLER[key](query[key], query);
    }

    return acc;
  }, {});
}

// TODO: Better reuse the handler staff
export function queryObjectToSearchCriteria(query) {
  const HANDLER = {
    [QUERY_OPTIONS.FIRST_NAME]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          first: item,
          last: getQueryObjectItem(queryObject, QUERY_OPTIONS.LAST_NAME, index),
          middle: getQueryObjectItem(queryObject, QUERY_OPTIONS.MIDDLE_NAME, index),
        };
      });
    },
    [QUERY_OPTIONS.PHONE_ADVANCED]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          number: item.toString(),
          country_code: getQueryObjectItem(queryObject, QUERY_OPTIONS.COUNTRY_CODE, index).toString(),
        };
      });
    },
    [QUERY_OPTIONS.PHONE]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          number: item.toString(),
          country_code: getQueryObjectItem(queryObject, QUERY_OPTIONS.COUNTRY_CODE, index).toString(),
        };
      });
    },
    [QUERY_OPTIONS.RELATIONSHIP]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          first: item,
          last: getQueryObjectItem(queryObject, QUERY_OPTIONS.RELATIONSHIP_LAST_NAME, index),
          middle: getQueryObjectItem(queryObject, QUERY_OPTIONS.RELATIONSHIP_MIDDLE_NAME, index),
        };
      });
    },
    [QUERY_OPTIONS.EDUCATIONS]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          school: item,
          degree: getQueryObjectItem(queryObject, QUERY_OPTIONS.DEGREE, index),
        };
      });
    },
    [QUERY_OPTIONS.JOB]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          org: item,
          title: getQueryObjectItem(queryObject, QUERY_OPTIONS.JOB_TITLE, index),
        };
      });
    },
    [QUERY_OPTIONS.EMAIL]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          email: item,
        };
      });
    },
    [QUERY_OPTIONS.DOB]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          dob: item,
        };
      });
    },
    [QUERY_OPTIONS.USERNAME]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          username: item,
        };
      });
    },
    [QUERY_OPTIONS.RELATIONSHIP_FIRST_NAME]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          first: item,
          middle: getQueryObjectItem(queryObject, QUERY_OPTIONS.RELATIONSHIP_MIDDLE_NAME, index),
          last: getQueryObjectItem(queryObject, QUERY_OPTIONS.RELATIONSHIP_LAST_NAME, index),
        };
      });
    },
    [QUERY_OPTIONS.LOCATION_STRING]: (values, queryObject) => {
      return values.map((item, index) => {
        return {
          id: index,
          locStr: item,
          locId: getQueryObjectItem(queryObject, QUERY_OPTIONS.LOCATION_ID, index),
          street: getQueryObjectItem(queryObject, QUERY_OPTIONS.STREET, index),
          house: getQueryObjectItem(queryObject, QUERY_OPTIONS.HOUSE, index),
        };
      });
    },
  };

  return Object.keys(query).reduce((acc, key) => {
    if (QUERY_TO_ADVANCED[key] && HANDLER[key] && query[key].length > 0) {
      acc[QUERY_TO_ADVANCED[key]] = HANDLER[key](query[key], query);
    }

    return acc;
  }, {});
}

export function isSoleAddressSearch(query) {
  const fields = ['house', 'street', 'loc_id'];

  return fields.every(field => query[field]);
}

export function makeArrQuery(query) {
  return Object.keys(query).reduce((acc, key) => {
    query[key] = query[key].display ? query[key].display : query[key]; //eslint-disable-line

    if (Array.isArray(query[key])) {
      acc[key] = query[key];
    } else {
      acc[key] = [query[key]];
    }

    return acc;
  }, {});
}

function createEmptyFreeFormObject() {
  return {
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    username: '',
    phone: '',
    title: '',
    org: '',
    location: '',
  };
}

export function filterSources({ sources, key = 'name' }) {
  if (!sources || sources.length === 0) {
    return sources;
  }
  const same = sources.reduce((acc, source) => {
    acc[source[key]] = acc[source[key]] || [];
    acc[source[key]].push(source);

    return acc;
  }, {});

  const filtered = Object.keys(same).reduce((all, sourceName) => {
    const withUrls = same[sourceName].filter(source => source.url || source.display_url);

    if (withUrls.length) {
      return [...all, ...withUrls];
    }

    return [...all, same[sourceName][0]];
  }, []);

  filtered.sort((s1, s2) => {
    if (s1.url == null && s2.url != null) {
      return 1;
    }

    if (s1.url != null && s2.url == null) {
      return -1;
    }
    return 0;
  });

  return filtered;
}

export function generateYearDisplay(rangeDate) {
  if (!rangeDate || (!rangeDate.start && !rangeDate.end)) return '';

  if (rangeDate.start) {
    if (rangeDate.end) {
      return `${rangeDate.start}-${rangeDate.end}`;
    }

    return `${gettext('since')} ${rangeDate.start}`;
  }

  return `${gettext('until')} ${rangeDate.end}`;
}
